<!--
  剖面视图 PropertyManager。

  UI 只消费 cad-core 控制器的普通状态快照，并将用户输入回写到统一控制器；
  不持有 Plane、Object3D、材质或方向箭头等渲染资源。
-->
<template>
  <div class="section-view-property" data-testid="section-view-property-section">
    <n-collapse :default-expanded-names="['options', 'section-0']">
      <n-collapse-item title="剖面选项" name="options">
        <div class="section-view-property__group">
          <n-checkbox
            :checked="state?.showCap ?? true"
            @update:checked="handleShowCapChange"
          >
            显示剖面盖
          </n-checkbox>
          <div class="section-view-property__hint">
            当前使用仅图形剖面；剖面盖不参与选择、测量或建模。
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item
        v-for="section in state?.sections ?? []"
        :key="section.index"
        :name="`section-${section.index}`"
      >
        <template #header>
          <div
            class="section-view-property__section-header"
            :class="{ 'section-view-property__section-header--active': state?.activeSectionIndex === section.index }"
            :data-testid="`section-view-header-${section.index}`"
            :aria-disabled="section.index === 2 && !isSecondSectionEnabled"
            @click="activateSection(section.index)"
          >
            <n-checkbox
              v-if="section.index > 0"
              :data-testid="`section-view-enable-${section.index}`"
              :checked="section.enabled"
              :disabled="section.index === 2 && !isSecondSectionEnabled"
              @click.stop
              @update:checked="setSectionEnabled(section.index, $event)"
            />
            <span>剖面 {{ section.index + 1 }}</span>
          </div>
        </template>

        <div
          class="section-view-property__group"
          :class="{ 'section-view-property__group--disabled': !section.enabled }"
          @pointerdown="activateSection(section.index)"
        >
          <div class="section-view-property__standard-planes" aria-label="标准基准面">
            <n-button
              size="tiny"
              :disabled="!section.enabled"
              title="前视基准面"
              @click="selectStandardPlane(section.index, 'front')"
            >前</n-button>
            <n-button
              size="tiny"
              :disabled="!section.enabled"
              title="上视基准面"
              @click="selectStandardPlane(section.index, 'top')"
            >上</n-button>
            <n-button
              size="tiny"
              :disabled="!section.enabled"
              title="右视基准面"
              @click="selectStandardPlane(section.index, 'right')"
            >右</n-button>
          </div>

          <div class="section-view-property__reference-row">
            <span>剖切面</span>
            <PuppyInputSelect
              class="section-view-property__reference-input"
              :items="section.hasReference ? [section] : []"
              :active="section.enabled && state?.activeSectionIndex === section.index"
              :active-index="section.hasReference && state?.activeSectionIndex === section.index ? 0 : -1"
              :selected-indices="section.hasReference ? [0] : []"
              :visible-rows="1"
              :disabled="!section.enabled"
              :context-menu-enabled="false"
              :show-delete-action="false"
              :show-delete-all-action="false"
              item-key-field="index"
              item-label-field="referenceLabel"
              empty-text="请选择平面实体面"
              :aria-label="`剖面 ${section.index + 1} 的剖切面`"
              @update:active="handleReferenceInputActiveUpdate(section.index, $event)"
            />
          </div>

          <div class="section-view-property__switch-row">
            <span>反向</span>
            <n-switch
              :data-testid="`section-view-reverse-${section.index}`"
              size="small"
              :value="section.reverse"
              :disabled="!section.enabled"
              @update:value="setReverse(section.index, $event)"
            />
          </div>

          <div class="section-view-property__number-row">
            <span>偏移</span>
            <n-input-number
              :value="Math.round(section.offsetMillimeters * 1000) / 1000"
              :disabled="!section.enabled"
              :precision="3"
              :step="1"
              size="small"
              @update:value="setOffsetMillimeters(section.index, $event)"
            >
              <template #suffix>mm</template>
            </n-input-number>
          </div>

          <div class="section-view-property__number-row">
            <span>X 旋转</span>
            <n-input-number
              :value="Math.round(section.rotationXDegrees * 100) / 100"
              :disabled="!section.enabled"
              :precision="2"
              :step="1"
              size="small"
              @update:value="setRotationXDegrees(section.index, $event)"
            >
              <template #suffix>°</template>
            </n-input-number>
          </div>

          <div class="section-view-property__number-row">
            <span>Y 旋转</span>
            <n-input-number
              :value="Math.round(section.rotationYDegrees * 100) / 100"
              :disabled="!section.enabled"
              :precision="2"
              :step="1"
              size="small"
              @update:value="setRotationYDegrees(section.index, $event)"
            >
              <template #suffix>°</template>
            </n-input-number>
          </div>

          <div class="section-view-property__color-row">
            <span>剖面颜色</span>
            <n-color-picker
              :value="section.color"
              :disabled="!section.enabled"
              :modes="['hex']"
              :show-alpha="false"
              size="small"
              @update:value="setColor(section.index, $event)"
            />
          </div>

          <n-alert v-if="section.errorMessage" type="error" :show-icon="false">
            {{ section.errorMessage }}
          </n-alert>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, shallowRef } from 'vue';
import {
  NAlert,
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NColorPicker,
  NInputNumber,
  NSwitch,
} from 'naive-ui';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import { notifySectionViewUiStateChanged, resolveSectionViewController } from '@/utils';
import type {
  SectionViewPropertySectionProps,
  SectionViewUiController,
  SectionViewUiStandardPlaneKind,
  SectionViewUiState,
} from '../../../../../types/editor';

const props = defineProps<SectionViewPropertySectionProps>();
const state = shallowRef<SectionViewUiState | null>(null);
let unsubscribeStateChanged: (() => void) | null = null;

/** 第二剖面是否启用；第三剖面必须依赖它。 */
const isSecondSectionEnabled = computed(() => state.value?.sections[1]?.enabled === true);

/**
 * 读取当前 Viewer 的剖面视图控制器。
 *
 * @returns 可用控制器；Viewer 尚未就绪时返回 null。
 */
function getController(): SectionViewUiController | null {
  return resolveSectionViewController(props.context.renderer);
}

/**
 * 从控制器同步普通状态快照。
 *
 * @returns 无返回值。
 */
function syncState(): void {
  state.value = getController()?.getState() ?? null;
  notifySectionViewUiStateChanged();
}

/**
 * 设置当前活动剖面。
 *
 * @param index 零基剖面序号。
 * @returns 无返回值。
 */
function activateSection(index: number): void {
  if (index === 2 && !isSecondSectionEnabled.value) {
    return;
  }
  getController()?.setActiveSection(index);
}

/**
 * 启用或关闭指定剖面。
 *
 * @param index 零基剖面序号。
 * @param enabled 是否启用。
 * @returns 无返回值。
 */
function setSectionEnabled(index: number, enabled: boolean): void {
  getController()?.setSectionEnabled(index, enabled);
}

/**
 * 选择标准基准面。
 *
 * @param index 零基剖面序号。
 * @param kind 标准基准面类型。
 * @returns 无返回值。
 */
function selectStandardPlane(index: number, kind: SectionViewUiStandardPlaneKind): void {
  getController()?.selectStandardPlane(index, kind);
}

/**
 * 激活指定剖面的实体面选择输入。
 *
 * @param index 零基剖面序号。
 * @param active 输入框是否请求激活。
 * @returns 无返回值。
 */
function handleReferenceInputActiveUpdate(index: number, active: boolean): void {
  if (!active) {
    return;
  }
  // cad-core 在整个编辑会话中持续接收实体面选择，UI 只负责切换选择写入的剖面槽位。
  activateSection(index);
}

/**
 * 设置剖切方向反转状态。
 *
 * @param index 零基剖面序号。
 * @param reverse 是否反向。
 * @returns 无返回值。
 */
function setReverse(index: number, reverse: boolean): void {
  getController()?.setReverse(index, reverse);
}

/**
 * 将可空数字输入归一化后写入偏移。
 *
 * @param index 零基剖面序号。
 * @param value 输入的毫米值。
 * @returns 无返回值。
 */
function setOffsetMillimeters(index: number, value: number | null): void {
  getController()?.setOffsetMillimeters(index, value ?? 0);
}

/**
 * 将可空数字输入归一化后写入 X 旋转角。
 *
 * @param index 零基剖面序号。
 * @param value 输入的角度值。
 * @returns 无返回值。
 */
function setRotationXDegrees(index: number, value: number | null): void {
  getController()?.setRotationXDegrees(index, value ?? 0);
}

/**
 * 将可空数字输入归一化后写入 Y 旋转角。
 *
 * @param index 零基剖面序号。
 * @param value 输入的角度值。
 * @returns 无返回值。
 */
function setRotationYDegrees(index: number, value: number | null): void {
  getController()?.setRotationYDegrees(index, value ?? 0);
}

/**
 * 设置剖面颜色。
 *
 * @param index 零基剖面序号。
 * @param color CSS 颜色值。
 * @returns 无返回值。
 */
function setColor(index: number, color: string): void {
  getController()?.setColor(index, color);
}

/**
 * 设置视觉剖面盖显示状态。
 *
 * @param show 是否显示盖面。
 * @returns 无返回值。
 */
function handleShowCapChange(show: boolean): void {
  getController()?.setShowCap(show);
}

onMounted(() => {
  const controller = getController();
  state.value = controller?.getState() ?? null;
  unsubscribeStateChanged = controller?.subscribeStateChanged(syncState) ?? null;
});

onBeforeUnmount(() => {
  unsubscribeStateChanged?.();
  unsubscribeStateChanged = null;
});
</script>

<style scoped lang="less">
.section-view-property {
  display: flex;
  flex-direction: column;
}

.section-view-property__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-view-property__group--disabled {
  opacity: 0.62;
}

.section-view-property__hint {
  color: var(--n-text-color-disabled);
  font-size: 11px;
  line-height: 1.5;
}

.section-view-property__section-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-view-property__section-header--active {
  color: var(--n-text-color-pressed);
}

.section-view-property__standard-planes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.section-view-property__reference-row,
.section-view-property__switch-row,
.section-view-property__number-row,
.section-view-property__color-row {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.section-view-property :deep(.section-view-property__reference-input.puppy-input-select) {
  min-height: 40px;
  max-height: 40px;
}

.section-view-property :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.section-view-property :deep(.n-collapse-item__content-inner) {
  padding-top: 4px;
}
</style>
